import { useCallback, useLayoutEffect } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import {
  useGetSubscriptionPaymentMethodQuery,
  useRemoveSubscriptionPaymentMethodMutation,
  useUpdateSubscriptionPaymentMethodMutation,
} from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { useNavigateBack } from '../../../../navigation';

export const useSweetpassPaymentMethodEdit = (
  props: UseSweetpassPaymentMethodEdit,
) => {
  const { paymentMethodId } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const navigateBack = useNavigateBack();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled', {
    listenForChanges: true,
  });

  // ─── Query ───────────────────────────────────────────────────────────

  const shouldPerformQuery = isSweetpassEnabled && Boolean(paymentMethodId);

  const [subscriptionPaymentMethodResponse] =
    useGetSubscriptionPaymentMethodQuery({
      pause: !shouldPerformQuery,
    });

  // ─── Mutations ───────────────────────────────────────────────────────

  const [{ fetching: isSettingActive }, updateSubscriptionPaymentMethod] =
    useUpdateSubscriptionPaymentMethodMutation();
  const [{ fetching: isRemoving }, removeSubscriptionPaymentMethod] =
    useRemoveSubscriptionPaymentMethodMutation();

  // ─── Derived Data ────────────────────────────────────────────────────

  const {
    data: sweetpassPaymentMethodResponseData,
    fetching: isSweetpassPaymentMethodLoading,
  } = subscriptionPaymentMethodResponse;

  const subscriptionId =
    sweetpassPaymentMethodResponseData?.getSubscriptions[0]?.id;

  const paymentMethod =
    sweetpassPaymentMethodResponseData?.paymentSubscriptionProfile?.creditCards?.find(
      (creditCard) => creditCard.id === paymentMethodId,
    );

  const hasActiveSubscription = Boolean(subscriptionId);

  // ─── Guard Effect ────────────────────────────────────────────────────

  useLayoutEffect(() => {
    if (isSweetpassEnabled && paymentMethodId) return;
    navigateBack();
  }, [isSweetpassEnabled, paymentMethodId, navigateBack]);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const setActive = useCallback(async () => {
    if (!subscriptionId || !paymentMethodId) return;

    const { data } = await updateSubscriptionPaymentMethod({
      subscriptionId,
      paymentMethodId,
    });

    const updatedSuccessfully =
      data?.updateSubscriptionPaymentMethod?.__typename ===
      'UpdateSubscriptionPaymentMethodSuccessResponse';

    if (!updatedSuccessfully) {
      addNoticeBanner({
        text: t('sweetpass-membership.payment-methods.update.error'),
        palette: 'caution',
      });

      return;
    }

    navigateBack();
  }, [
    subscriptionId,
    paymentMethodId,
    t,
    updateSubscriptionPaymentMethod,
    addNoticeBanner,
    navigateBack,
  ]);

  const remove = useCallback(async () => {
    if (!paymentMethodId) return;

    const { data } = await removeSubscriptionPaymentMethod({
      paymentMethodId,
    });

    const removedSuccessfully =
      data?.removeSubscriptionPaymentMethod.__typename ===
      'DeleteSubscriptionPaymentMethodSuccess';

    if (!removedSuccessfully) {
      addNoticeBanner({
        text: t('sweetpass-membership.payment-methods.remove.error'),
        palette: 'caution',
      });

      return;
    }

    navigateBack();
  }, [
    paymentMethodId,
    t,
    removeSubscriptionPaymentMethod,
    addNoticeBanner,
    navigateBack,
  ]);

  return {
    hasActiveSubscription,
    paymentMethod,
    isSettingActive,
    isRemoving,
    isSweetpassPaymentMethodLoading,
    setActive,
    remove,
  };
};

// ─── Types ─────────────────────────────────────────────────────────────

type UseSweetpassPaymentMethodEdit = {
  readonly paymentMethodId: string;
};
